import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Lock, Unlock, Save } from "lucide-react";

const ALL_PAIRS = [
  "XAU_USD", "USD_JPY", "EUR_USD", "GBP_USD", "AUD_USD",
  "USD_CAD", "NZD_USD", "USD_CHF", "EUR_JPY", "GBP_JPY", "XAG_USD",
  "BTC_USD", "ETH_USD", "US30", "US100", "US500",
];

/**
 * Admin modal — edit any user's BotSettings + strategy and lock the user out.
 *
 * Props:
 *  - user: { id, email, name }
 *  - strategies: [{ id, name, timeframe, params: {...defaults} }]
 *  - onClose()
 *  - onSaved()  // optional — re-fetches the user list
 */
export default function UserSettingsModal({ user, strategies, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/settings`);
        if (cancel) return;
        setS(data);
        setLocked(!!data.locked_by_admin);
      } catch {
        toast.error("Failed to load user settings");
      }
    })();
    return () => { cancel = true; };
  }, [user.id]);

  if (!s) {
    return (
      <Backdrop onClose={onClose}>
        <div className="text-zinc-500 text-sm">Loading…</div>
      </Backdrop>
    );
  }

  const set = (k, v) => setS({ ...s, [k]: v });
  const setParam = (k, v) => setS({ ...s, strategy_params: { ...(s.strategy_params || {}), [k]: v } });
  const togglePair = (p) => {
    const pairs = (s.pairs || []).includes(p)
      ? s.pairs.filter((x) => x !== p)
      : [...(s.pairs || []), p];
    set("pairs", pairs);
  };

  const save = async () => {
    setBusy(true);
    try {
      // Sanitize: replace any NaN / non-finite numbers with sensible defaults
      // before sending. NaN serialises to `null` in JSON which the backend
      // rejects with 422. This keeps the save bulletproof against partial input.
      const clean = JSON.parse(JSON.stringify(s, (_k, v) =>
        (typeof v === "number" && !Number.isFinite(v)) ? 0 : v
      ));
      await api.put(`/admin/users/${user.id}/settings`, {
        settings: { ...clean, execution_broker: "mt5" },
        locked,
      });
      toast.success(`Settings applied to ${user.email}${locked ? " (locked)" : ""}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/settings/unlock`);
      toast.success("User can now edit their own settings");
      setLocked(false);
      onSaved && onSaved();
    } catch {
      toast.error("Unlock failed");
    } finally {
      setBusy(false);
    }
  };

  const activeStrat = strategies.find((x) => x.id === s.strategy_id);
  const params = s.strategy_params || {};

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="admin-user-settings-modal"
           className="bg-[#0e0e10] border border-white/10 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0e0e10] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Edit user settings</div>
            <div className="font-mono text-sm text-zinc-100 mt-0.5 truncate max-w-md">{user.email}</div>
          </div>
          <button onClick={onClose} data-testid="admin-settings-modal-close"
                  className="text-zinc-500 hover:text-zinc-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Strategy picker */}
          <Section label="Strategy">
            <select data-testid="admin-strategy-select"
                    value={s.strategy_id || ""}
                    onChange={(e) => set("strategy_id", e.target.value)}
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none">
              {strategies.map((st) => (
                <option key={st.id} value={st.id}>{st.name} · {st.timeframe}</option>
              ))}
            </select>
            {activeStrat?.description && (
              <p className="text-xs text-zinc-500 mt-2">{activeStrat.description}</p>
            )}
          </Section>

          {/* Pairs */}
          <Section label="Monitored Pairs">
            <div className="flex flex-wrap gap-1.5">
              {ALL_PAIRS.map((p) => {
                const active = (s.pairs || []).includes(p);
                return (
                  <button key={p} type="button" onClick={() => togglePair(p)}
                          data-testid={`admin-pair-toggle-${p}`}
                          className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm border transition-colors ${
                            active
                              ? "border-teal-400/40 bg-teal-400/10 text-teal-300"
                              : "border-white/10 text-zinc-500 hover:text-zinc-300"
                          }`}>
                    {p.replace("_", "")}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Core sizing */}
          <Section label="Sizing & Mode">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Lot Size">
                <NumInput testid="admin-lot-size" value={s.lot_size} step={0.01} min={0.01}
                          onChange={(v) => set("lot_size", v)} />
              </Field>
              <Field label="Trades / Signal">
                <NumInput testid="admin-trades-per-signal" value={s.trades_per_signal} step={1} min={1}
                          onChange={(v) => set("trades_per_signal", v)} />
              </Field>
              <Field label="Stop Loss (pips)">
                <NumInput testid="admin-stop-loss-pips" value={s.stop_loss_pips} step={1} min={1}
                          onChange={(v) => set("stop_loss_pips", v)} />
              </Field>
              <Field label="Take Profit (pips)">
                <NumInput testid="admin-take-profit-pips" value={s.take_profit_pips} step={1} min={1}
                          onChange={(v) => set("take_profit_pips", v)} />
              </Field>
              <Field label="Spread Buffer (pips)">
                <NumInput testid="admin-spread-buffer" value={s.spread_buffer_pips || 0} step={0.5} min={0}
                          onChange={(v) => set("spread_buffer_pips", v)} />
              </Field>
              <Field label="Mode">
                <select value={s.mode || "demo"} onChange={(e) => set("mode", e.target.value)}
                        data-testid="admin-mode-select"
                        className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                  <option value="demo">demo</option>
                  <option value="live">live</option>
                </select>
              </Field>
              <Field label="Bot Enabled">
                <select value={s.bot_enabled ? "true" : "false"}
                        onChange={(e) => set("bot_enabled", e.target.value === "true")}
                        data-testid="admin-bot-enabled-select"
                        className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                  <option value="false">off</option>
                  <option value="true">on</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Strategy-specific params — generic key/value editor based on activeStrat.default_params */}
          {activeStrat?.default_params && Object.keys(activeStrat.default_params).length > 0 && (
            <Section label={`${activeStrat.name} — parameters`}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(activeStrat.default_params).map(([k, defaultVal]) => {
                  const cur = params[k] !== undefined ? params[k] : defaultVal;
                  return (
                    <Field key={k} label={k}>
                      <NumInput testid={`admin-param-${k}`} value={cur} step={typeof cur === "number" && cur < 1 ? 0.01 : 1}
                                onChange={(v) => setParam(k, v)} />
                    </Field>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Risk controls */}
          <Section label="Account Risk Controls">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max Drawdown %">
                <NumInput testid="admin-max-dd" value={s.risk_max_drawdown_pct || 0} step={0.1} min={0}
                          onChange={(v) => set("risk_max_drawdown_pct", v)} />
              </Field>
              <Field label="Account TP %">
                <NumInput testid="admin-acct-tp" value={s.risk_take_profit_pct || 0} step={0.1} min={0}
                          onChange={(v) => set("risk_take_profit_pct", v)} />
              </Field>
            </div>
            <div className="mt-3 space-y-2">
              <Toggle testid="admin-be-1r" label="Move SL → break-even at 1R"
                      value={!!s.be_at_1r_enabled} onChange={(v) => set("be_at_1r_enabled", v)} />
              {s.be_at_1r_enabled && (
                <Field label="BE buffer (pips)">
                  <NumInput testid="admin-be-buffer" value={s.be_buffer_pips || 1} step={0.1} min={0}
                            onChange={(v) => set("be_buffer_pips", v)} />
                </Field>
              )}
              <Toggle testid="admin-be-tp" label="Move SL → break-even at % of TP"
                      value={!!s.be_at_tp_enabled} onChange={(v) => set("be_at_tp_enabled", v)} />
              {s.be_at_tp_enabled && (
                <Field label="% of TP travelled to trigger BE">
                  <NumInput testid="admin-be-tp-pct" value={s.be_at_tp_pct || 50} step={1} min={10} max={95}
                            onChange={(v) => set("be_at_tp_pct", v)} />
                </Field>
              )}
            </div>
          </Section>

          {/* Lock toggle */}
          <Section label="Access control">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)}
                     data-testid="admin-lock-toggle"
                     className="mt-0.5 accent-amber-400" />
              <div>
                <div className="text-sm text-zinc-100 flex items-center gap-1.5">
                  {locked ? <Lock className="w-3.5 h-3.5 text-amber-400" /> : <Unlock className="w-3.5 h-3.5 text-zinc-500" />}
                  Lock these settings — user cannot change them
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  When locked, the user&apos;s <span className="text-zinc-400">/settings</span> page becomes read-only
                  and any attempt to PUT settings is rejected by the API.
                </div>
              </div>
            </label>
          </Section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#0e0e10] border-t border-white/10 px-6 py-4 flex flex-wrap items-center justify-between gap-3 z-10">
          <button onClick={unlock} disabled={busy || !s.locked_by_admin}
                  data-testid="admin-unlock-only-btn"
                  className="text-xs font-mono uppercase tracking-widest px-3 py-2 border border-white/10 text-zinc-400 hover:text-zinc-100 hover:bg-white/5 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed">
            <Unlock className="w-3.5 h-3.5 inline mr-1.5" /> Unlock only
          </button>
          <div className="flex gap-2">
            <button onClick={onClose}
                    className="text-xs font-mono uppercase tracking-widest px-4 py-2 text-zinc-500 hover:text-zinc-200 rounded-sm">
              Cancel
            </button>
            <button onClick={save} disabled={busy}
                    data-testid="admin-save-user-settings-btn"
                    className="text-xs font-mono uppercase tracking-widest px-5 py-2 bg-amber-400 text-black hover:bg-amber-300 rounded-sm disabled:opacity-40 flex items-center gap-2">
              <Save className="w-3.5 h-3.5" />
              {busy ? "Saving…" : locked ? "Apply & lock" : "Apply"}
            </button>
          </div>
        </div>
      </div>
    </Backdrop>
  );
}

function Backdrop({ onClose, children }) {
  return (
    <div onClick={onClose}
         className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl">
        {children}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="border border-white/10 bg-[#121214] rounded-sm p-4 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function NumInput({ testid, value, onChange, step = 1, min, max }) {
  // Store a local "draft" string so users can type decimals like "1." mid-edit
  // without the value snapping back. Commit to parent only when we have a valid number.
  const [draft, setDraft] = useState(
    value === undefined || value === null || Number.isNaN(value) ? "" : String(value)
  );
  // Keep draft in sync if the parent value changes externally (e.g. strategy switch)
  useEffect(() => {
    const parsed = parseFloat(draft);
    if (
      value !== undefined && value !== null && !Number.isNaN(value) &&
      (Number.isNaN(parsed) || parsed !== value)
    ) {
      setDraft(String(value));
    }
  }, [value]);
  return (
    <input
      type="number" step={step} min={min} max={max}
      data-testid={testid}
      value={draft}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw === "" || raw === "-" || raw === ".") {
          // Don't commit incomplete numbers — let the user keep typing
          return;
        }
        const n = parseFloat(raw);
        if (!Number.isNaN(n)) onChange(n);
      }}
      onBlur={() => {
        // On blur, normalise to a real number (fallback to 0 if blank/invalid)
        const n = parseFloat(draft);
        if (Number.isNaN(n)) {
          setDraft("0");
          onChange(0);
        }
      }}
      className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none tabular-nums"
    />
  );
}

function Toggle({ testid, label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-200">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)}
             data-testid={testid} className="accent-teal-400" />
      {label}
    </label>
  );
}
